import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';

import {
  ensureJoinedChatThread,
  extractLiveWatchFromRunResponse,
} from '@/components/ConfigInternalTasks/scheduleTaskJoin';
import { isStaffRole } from '@/pages/Launchpad/utils/permissions';
import { bindServices } from '@/services/api';
import { getById as getByIdPrimary, getList } from '@/services/global';
import SocketService from '@/services/socket.io';
import { createCrudModel } from '@/utils/core/dva';
import CoreQueries from '@/utils/input/queries';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants/constants';

import {
  accessControlPatchValue,
  normalizeScheduleCollaborators,
  resolveUsersAsCollaborators,
  toBaseConfigSimpleUsers,
} from './scheduleSharing';

const LAUNCHPAD_FIRST_PAGE = 60;

/** Normalize create response → entity (or `{ id }`). */
const unwrapCreatedEntity = (res) => {
  if (typeof res === 'string' && res) return { id: res };
  if (res?.data?.id != null) return res.data;
  if (typeof res?.data === 'string' && res.data) return { id: res.data };
  if (res?.id != null) return res;
  if (res?.data && typeof res.data === 'object') return res.data;
  if (res && typeof res === 'object') return res;
  return {};
};

/** Prefer schedule id; never confuse it with `aiAgentId`. */
const resolveScheduleId = (entity, aiAgentId) => {
  const id = entity?.id;
  if (id == null || id === '') return null;
  if (aiAgentId != null && String(id) === String(aiAgentId)) return null;
  return id;
};

const namespace = 'aiAgentSchedules';
const fetchRoute = 'ai/schedule';
const fetchParams = 'AiAgentSchedule';
const api = bindServices('aiAgentSchedules');

const isNotFoundError = (error) => {
  const status = error?.status || error?.response?.status || error?.data?.status;
  const code = error?.code || error?.data?.code;
  return Number(status) === 404 || String(code) === '404';
};

const findDeletedConflict = (error) => {
  const validationErrors = error?.validationErrors || [];
  return validationErrors.find(
    (ve) =>
      ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS && ve.metadata?.id,
  );
};

/**
 * GET schedule by id on primary (`AUTOMY-Read-Priority: primary`).
 * Same contract as `services/global.getList` / agents after mutate.
 *
 * @param {boolean} [full] When true, omit `$select` so the JSON body can
 *   include `accessControl` / `collaborators` (not OData EDM properties —
 *   `$select`ing them 400s).
 */
function* fetchSchedulePrimary(call, scheduleId, full = false) {
  if (!scheduleId) return null;
  const params = full
    ? {}
    : (() => {
        const { expand: _expand, ...itemQuery } = CoreQueries[fetchParams]?.item || {};
        return itemQuery;
      })();
  const fresh = yield call(getByIdPrimary, {
    route: fetchRoute,
    id: scheduleId,
    params,
    readPriority: true,
  });
  const body = fresh?.data && typeof fresh.data === 'object' ? fresh.data : fresh;
  return body && typeof body === 'object' ? body : null;
}

/**
 * Refresh Launchpad jobs list on primary after create/update/delete/activate.
 * Done in the saga (not the React `success` watcher) because closing the form
 * resets `success` via `queryShowForm` and races that watcher.
 */
function* refreshJobsLaunchpad(call, put, select) {
  const roles = yield select((s) => s.user?.currentUser?.roles);
  const includeInactive = isStaffRole(roles);
  try {
    const listRes = yield call(getList, {
      route: `${fetchRoute}/paged`,
      params: {
        page: 1,
        limit: LAUNCHPAD_FIRST_PAGE,
        ...CoreQueries[fetchParams].paged,
        ...(!includeInactive ? { filter: 'isActive eq true' } : {}),
      },
      readPriority: true,
    });
    const items = Array.isArray(listRes?.items)
      ? listRes.items
      : Array.isArray(listRes)
      ? listRes
      : [];
    const total =
      typeof listRes?.totalCount === 'number'
        ? listRes.totalCount
        : typeof listRes?.count === 'number'
        ? listRes.count
        : items.length;

    yield put({ type: 'launchpad/homeStart', payload: { type: 'jobs' } });
    yield put({
      type: 'launchpad/homeSave',
      payload: {
        type: 'jobs',
        items,
        groups: [],
        favorites: [],
        progress: { itemsLoaded: items.length, itemsTotal: total },
      },
    });
  } catch {
    yield put({
      type: 'launchpad/fetchHome',
      payload: { type: 'jobs', includeInactive, readPriority: true },
    });
  }
}

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: 'aiAgentSchedule',
  api,
  features: { import: false, bulkActions: false, listCache: true },
  state: {
    showTasks: false,
    tasksScheduleId: null,
    tasksScheduleName: null,
    /** When set, ScheduleTasksDrawer opens the task detail drawer for this id. */
    tasksOpenTaskId: null,
    /** Bumps on each deep-link so the same taskId can re-open detail. */
    tasksOpenTaskNonce: 0,
    /** When true, only the detail drawer opens (no tasks history list). */
    tasksDetailOnly: false,
    /** Bumps when a schedule Run succeeds so Launchpad can refresh executions. */
    executionsRefreshAt: 0,
    /**
     * Active live subscription after Ejecutar — board joins this ChatThread
     * immediately (does not wait for the executions list refresh).
     */
    liveWatch: null,
    restoreScheduleModal: {
      visible: false,
      message: '',
      scheduleId: null,
    },
    /** "Ejecutar ahora" modal (UX §4.1.1): per-run overrides before POST /run. */
    runModal: {
      visible: false,
      scheduleId: null,
      chatThreadId: null,
      aiAgentId: null,
    },
    /**
     * AccessControl vs Collaborator drawers (embedded on the schedule —
     * PATCH `/ai/schedule/{id}`, not a separate `/config/collaborators` route).
     */
    collaboratorsSchedule: null,
    collaborators: [],
    accessControlSchedule: null,
  },
  effects: {
    /**
     * GET ai/schedule/{id} — soft-deleted / missing schedules return 404.
     * Close the drawer and refresh Launchpad so stale job rows disappear.
     */
    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        // Never pass `$expand=aiAgent` — it can overwrite schedule `name` / `id`
        // with the agent's (queries/aiAgentSchedule.js). Resolve agent separately.
        const baseParams = payload.detail
          ? CoreQueries[fetchParams]?.itemDetail
          : CoreQueries[fetchParams]?.item;
        const { expand: _ignoredExpand, ...safeExtraParams } = payload?.params || {};
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {
            ...baseParams,
            ...safeExtraParams,
          },
        });

        let item = res && typeof res === 'object' ? { ...res } : res;
        const agentId = item?.aiAgentId;
        if (agentId && !item?.aiAgent?.name) {
          try {
            const agent = yield call(getByIdPrimary, {
              route: 'ai/agent',
              id: agentId,
              params: { select: 'id,name,iconUrl,isActive' },
            });
            const agentBody = agent?.data && typeof agent.data === 'object' ? agent.data : agent;
            if (agentBody?.id) {
              item = {
                ...item,
                aiAgent: {
                  id: agentBody.id,
                  name: agentBody.name,
                  iconUrl: agentBody.iconUrl,
                  isActive: agentBody.isActive,
                },
              };
            }
          } catch {
            // Non-fatal: EntitySelector can still enrich from aiAgentId alone.
          }
        }

        // accessControl / collaborators are not OData $select-able — hydrate
        // from a full GET-by-id so PUT preserve + form sharing sections work.
        try {
          const full = yield* fetchSchedulePrimary(call, payload.id, true);
          if (full) {
            item = {
              ...item,
              accessControl: full.accessControl ?? null,
              collaborators: full.collaborators ?? null,
              ownerUserId: full.ownerUserId ?? item.ownerUserId ?? null,
            };
          }
        } catch {
          // Non-fatal: drawers can still hydrate on open.
        }

        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        if (isNotFoundError(e)) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
          yield put({ type: 'clearListCache' });
          // Triggers Launchpad refresh via useLaunchpadAddAction (false→true).
          yield put({ type: 'success', payload: { success: true } });
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *fetchCreate({ payload }, { put, call, select }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, {
          route: fetchRoute,
          data: payload.data,
        });
        yield put({ type: 'clearListCache' });

        const created = unwrapCreatedEntity(res);
        const createdItem = { ...payload.data, ...created };
        const aiAgentId = createdItem.aiAgentId || payload.data?.aiAgentId;
        const scheduleId = resolveScheduleId(createdItem, aiAgentId);

        if (scheduleId) {
          let hydrated = { ...createdItem, id: scheduleId };
          try {
            const body = yield* fetchSchedulePrimary(call, scheduleId, true);
            if (body) hydrated = { ...hydrated, ...body, id: scheduleId };
          } catch {
            // Non-fatal: still prepend the create payload.
          }
          yield put({ type: 'prependCreated', payload: hydrated });
        }

        yield* refreshJobsLaunchpad(call, put, select);

        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const itemDeletedError = findDeletedConflict(e);
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || e?.message;
          yield put({
            type: 'showRestoreScheduleModal',
            payload: { message, scheduleId: itemDeletedError.metadata.id },
          });
          return;
        }
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call, select }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({ type: 'clearListCache' });

        const scheduleId = payload.data?.id;
        if (scheduleId) {
          try {
            const body = yield* fetchSchedulePrimary(call, scheduleId, true);
            if (body) yield put({ type: 'queryItem', payload: { ...payload.data, ...body } });
          } catch {
            // Non-fatal: Launchpad refresh below still hits primary.
          }
        }

        yield* refreshJobsLaunchpad(call, put, select);

        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      try {
        const { id, view } = payload || {};
        yield call(api.remove, { route: fetchRoute, id });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield put({ type: 'clearListCache' });
        // List on primary so the deleted row stays gone (replica lag).
        yield* refreshJobsLaunchpad(call, put, select);
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    },

    /**
     * Pause / resume schedule programming (UX §7.6).
     * `POST ai/schedule/activate/{id}/{true|false}` — only toggles future
     * automatic materialization (`IsActive`). Does not pause tasks in Processing.
     * Manual Run remains allowed while the schedule is not soft-deleted.
     */
    *fetchActivate({ payload }, { put, call, select }) {
      const { id } = payload || {};
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(api.activate, { route: fetchRoute, id, isActive: payload.state });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'aiAgentScheduleActivate', payload });

        // Confirm IsActive from primary, then refresh Launchpad list.
        try {
          const body = yield* fetchSchedulePrimary(call, id);
          if (body && typeof body.isActive === 'boolean') {
            yield put({
              type: 'launchpad/syncItemActive',
              payload: { type: 'jobs', id, isActive: body.isActive },
            });
          }
        } catch {
          // Non-fatal.
        }
        yield* refreshJobsLaunchpad(call, put, select);
      } catch (e) {
        // Revert optimistic tile if pause/resume failed.
        if (typeof payload?.state === 'boolean') {
          yield put({
            type: 'launchpad/syncItemActive',
            payload: { type: 'jobs', id, isActive: !payload.state },
          });
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *showTasks({ payload }, { put }) {
      yield put({ type: 'querySetTasks', payload });
    },

    /**
     * `schedule-changed` on the `ai-agent-schedule` socket channel (UX §5.5):
     * create/update/activate/deactivate/soft-delete/restore from anywhere
     * (other tab, another admin, backend). Socket is best-effort — reconcile
     * via HTTP on primary, same as after local mutations.
     */
    *scheduleChangedFromSocket(_action, { call, put, select }) {
      yield put({ type: 'clearListCache' });
      yield* refreshJobsLaunchpad(call, put, select);
    },

    /** POST ai/schedule/{id}/run — manual execution (202 new / 200 idempotent). */
    *fetchRun({ payload }, { put, call }) {
      try {
        const id = payload?.id;
        if (!id) return;

        // Wake the socket immediately on Ejecutar — join as soon as we know the room.
        try {
          SocketService.getInstance().ensureConnected?.();
        } catch (e) {
          // Non-fatal: join retries after Run response / refresh.
        }

        const knownThread = payload?.chatThreadId || null;
        if (knownThread) {
          yield put({
            type: 'setLiveWatch',
            payload: {
              scheduleId: id,
              taskId: null,
              chatThreadId: knownThread,
              pending: true,
              at: Date.now(),
            },
          });
          ensureJoinedChatThread(knownThread);
        }

        // UX §4.1.1: always send an idempotencyKey — network retries of the
        // same click reuse the task (200) instead of materializing a second
        // execution (202). `instructions`/`documents` override this run only.
        const idempotencyKey =
          payload?.idempotencyKey ||
          `run-${id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

        const response = yield call(api.runAiAgentSchedule, {
          id,
          // Always send a JSON object body (even empty) — some hosts reject bare POST.
          data: {
            idempotencyKey,
            ...(payload?.instructions ? { instructions: payload.instructions } : {}),
            ...(payload?.documents ? { documents: payload.documents } : {}),
            ...(payload?.userTaskId ? { userTaskId: payload.userTaskId } : {}),
            ...(Array.isArray(payload?.runtimeReferences) && payload.runtimeReferences.length
              ? { runtimeReferences: payload.runtimeReferences }
              : {}),
          },
        });

        let watch = extractLiveWatchFromRunResponse(response, id);
        if (!watch.chatThreadId && knownThread) {
          watch = { ...watch, chatThreadId: knownThread };
        }

        // If Run returned a taskId but no thread yet, recover once via runtime-state.
        if (watch.taskId && !watch.chatThreadId) {
          try {
            const runtime = yield call(
              api.getAiAgentScheduleTaskRuntimeState,
              { id: watch.taskId },
              { suppressErrorNotification: true },
            );
            if (runtime?.chatThreadId) {
              watch = { ...watch, chatThreadId: runtime.chatThreadId };
            }
          } catch (e) {
            // Socket join will retry after executions refresh brings chatThreadId.
          }
        }

        yield put({
          type: 'setLiveWatch',
          payload: {
            scheduleId: id,
            taskId: watch.taskId,
            chatThreadId: watch.chatThreadId,
            pending: false,
            at: Date.now(),
          },
        });
        if (watch.chatThreadId) {
          ensureJoinedChatThread(watch.chatThreadId);
        }

        yield put({ type: 'closeRunModal' });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'bumpExecutionsRefresh' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        if (typeof payload?.onSuccess === 'function') {
          payload.onSuccess(watch);
        }
      } catch (e) {
        yield put({ type: 'clearLiveWatch' });
        // Soft-deleted / other-tenant / missing schedules all surface as 404 on Run
        // (UX guide §4.1.1). Refresh the Launchpad list so stale cards disappear.
        if (isNotFoundError(e)) {
          yield put({ type: 'clearListCache' });
          yield put({ type: 'success', payload: { success: true } });
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    /**
     * Open collaborators drawer. Always hydrate via GET-by-id without
     * `$select` — `collaborators` is not an OData EDM property.
     */
    *openCollaboratorsDrawer({ payload }, { put, call }) {
      const schedule = payload || null;
      const scheduleId = schedule?.id;
      let collaborators = normalizeScheduleCollaborators(schedule?.collaborators);
      let merged = schedule;
      if (scheduleId) {
        try {
          const body = yield* fetchSchedulePrimary(call, scheduleId, true);
          if (body) {
            collaborators = normalizeScheduleCollaborators(body.collaborators);
            merged = { ...schedule, ...body, id: scheduleId };
          }
        } catch {
          // Fall through with tile/local list.
        }
      }
      yield put({
        type: 'queryOpenCollaboratorsDrawer',
        payload: { schedule: merged, collaborators },
      });
    },

    *addScheduleCollaborators({ payload }, { put, call, select }) {
      const scheduleId =
        payload?.scheduleId || (yield select((s) => s.aiAgentSchedules.collaboratorsSchedule?.id));
      if (!scheduleId || !Array.isArray(payload?.userIds) || !payload.userIds.length) return;
      try {
        const current = yield select((s) => s.aiAgentSchedules.collaborators || []);
        const existingIds = new Set(current.map((c) => c.id).filter(Boolean));
        const toAdd = payload.userIds.filter((id) => id && !existingIds.has(id));
        if (!toAdd.length) return;

        const resolved = yield call(resolveUsersAsCollaborators, toAdd);
        const next = toBaseConfigSimpleUsers([...current, ...resolved]);
        yield call(api.updateById, {
          route: fetchRoute,
          id: scheduleId,
          data: { collaborators: next },
        });
        const normalized = normalizeScheduleCollaborators(next);
        yield put({ type: 'querySetCollaborators', payload: normalized });
        yield put({
          type: 'mergeScheduleSharing',
          payload: { id: scheduleId, collaborators: normalized },
        });
        yield put({ type: 'clearListCache' });
        yield* refreshJobsLaunchpad(call, put, select);
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
        throw e;
      }
    },

    *removeScheduleCollaborator({ payload }, { put, call, select }) {
      const scheduleId =
        payload?.scheduleId || (yield select((s) => s.aiAgentSchedules.collaboratorsSchedule?.id));
      const userId = payload?.userId;
      if (!scheduleId || !userId) return;
      try {
        const current = yield select((s) => s.aiAgentSchedules.collaborators || []);
        const next = toBaseConfigSimpleUsers(current.filter((c) => c.id !== userId));
        yield call(api.updateById, {
          route: fetchRoute,
          id: scheduleId,
          data: { collaborators: next },
        });
        const normalized = normalizeScheduleCollaborators(next);
        yield put({ type: 'querySetCollaborators', payload: normalized });
        yield put({
          type: 'mergeScheduleSharing',
          payload: { id: scheduleId, collaborators: normalized },
        });
        yield put({ type: 'clearListCache' });
        yield* refreshJobsLaunchpad(call, put, select);
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
        throw e;
      }
    },

    *openAccessControlDrawer({ payload }, { put, call }) {
      const schedule = payload || null;
      const scheduleId = schedule?.id;
      let merged = schedule;
      if (scheduleId) {
        try {
          // Full GET — `accessControl` is not OData `$select`-able.
          const body = yield* fetchSchedulePrimary(call, scheduleId, true);
          if (body) {
            merged = { ...schedule, ...body, id: scheduleId };
          }
        } catch {
          // Fall through with tile payload.
        }
      }
      yield put({ type: 'queryOpenAccessControlDrawer', payload: merged });
    },

    *patchScheduleAccessControl({ payload }, { put, call, select }) {
      const scheduleId =
        payload?.scheduleId || (yield select((s) => s.aiAgentSchedules.accessControlSchedule?.id));
      if (!scheduleId) return;
      try {
        const accessControl = accessControlPatchValue(payload?.accessControl);
        yield call(api.updateById, {
          route: fetchRoute,
          id: scheduleId,
          data: { accessControl },
        });
        yield put({
          type: 'mergeScheduleSharing',
          payload: { id: scheduleId, accessControl },
        });
        yield put({
          type: 'queryOpenAccessControlDrawer',
          payload: {
            ...(yield select((s) => s.aiAgentSchedules.accessControlSchedule)),
            id: scheduleId,
            accessControl,
          },
        });
        yield put({ type: 'clearListCache' });
        yield* refreshJobsLaunchpad(call, put, select);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
        throw e;
      }
    },

    /** POST ai/schedule/restore — soft-deleted schedule; leaves isActive=false. */
    *restoreSchedule({ payload }, { put, call }) {
      try {
        const scheduleId = payload?.scheduleId;
        if (!scheduleId) return;
        yield call(api.apiRoute, {
          route: `${fetchRoute}/restore`,
          method: 'post',
          data: [scheduleId],
        });
        yield put({ type: 'closeRestoreScheduleModal' });
        yield put({ type: 'clearListCache' });
        yield put({
          type: 'showForm',
          payload: { state: false, item: null, detail: false, id: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'success', payload: { success: true } });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },
  },
  reducers: {
    prependCreated(state, { payload }) {
      const created = {
        ...payload,
        isActive: payload.isActive === true,
      };
      const withoutDup = state.list.filter((item) => item.id !== created.id);
      return { ...state, list: [created, ...withoutDup] };
    },

    querySetTasks(state, { payload }) {
      const opening = !!payload.state;
      const taskId = opening ? payload.taskId || null : null;
      return {
        ...state,
        showTasks: opening,
        ...(opening
          ? {
              tasksScheduleId: payload.id,
              tasksScheduleName: payload.name || null,
              tasksOpenTaskId: taskId,
              tasksOpenTaskNonce: taskId ? Date.now() : state.tasksOpenTaskNonce,
              tasksDetailOnly: !!payload.detailOnly && !!taskId,
            }
          : {
              tasksOpenTaskId: null,
              tasksDetailOnly: false,
            }),
      };
    },

    showRunModal(state, { payload }) {
      return {
        ...state,
        runModal: {
          visible: true,
          scheduleId: payload?.scheduleId || null,
          chatThreadId: payload?.chatThreadId || null,
          aiAgentId: payload?.aiAgentId || null,
        },
      };
    },

    closeRunModal(state) {
      return {
        ...state,
        runModal: {
          visible: false,
          scheduleId: null,
          chatThreadId: null,
          aiAgentId: null,
        },
      };
    },

    showRestoreScheduleModal(state, { payload }) {
      return {
        ...state,
        restoreScheduleModal: {
          visible: true,
          message: payload?.message || '',
          scheduleId: payload?.scheduleId || null,
        },
      };
    },

    closeRestoreScheduleModal(state) {
      return {
        ...state,
        restoreScheduleModal: {
          visible: false,
          message: '',
          scheduleId: null,
        },
      };
    },

    bumpExecutionsRefresh(state) {
      return { ...state, executionsRefreshAt: Date.now() };
    },

    setLiveWatch(state, { payload }) {
      if (!payload) return { ...state, liveWatch: null };
      return {
        ...state,
        liveWatch: {
          scheduleId: payload.scheduleId || null,
          taskId: payload.taskId || null,
          chatThreadId: payload.chatThreadId || null,
          pending: !!payload.pending,
          at: payload.at || Date.now(),
        },
      };
    },

    clearLiveWatch(state) {
      return { ...state, liveWatch: null };
    },

    queryOpenCollaboratorsDrawer(state, { payload }) {
      return {
        ...state,
        collaboratorsSchedule: payload?.schedule || null,
        collaborators: payload?.collaborators || [],
      };
    },

    closeCollaboratorsDrawer(state) {
      return { ...state, collaboratorsSchedule: null, collaborators: [] };
    },

    querySetCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },

    queryOpenAccessControlDrawer(state, { payload }) {
      return { ...state, accessControlSchedule: payload || null };
    },

    closeAccessControlDrawer(state) {
      return { ...state, accessControlSchedule: null };
    },

    /**
     * Keep the open form item + drawer schedule slices in sync after a
     * sharing PATCH so a later PUT from the form preserves the latest
     * accessControl / collaborators (PUT is full replace, UX §4.3).
     */
    mergeScheduleSharing(state, { payload }) {
      const { id, accessControl, collaborators } = payload || {};
      if (!id) return state;
      let next = { ...state };
      if (next.item?.id === id) {
        next = {
          ...next,
          item: {
            ...next.item,
            ...(accessControl !== undefined ? { accessControl } : {}),
            ...(collaborators !== undefined ? { collaborators } : {}),
          },
        };
      }
      if (next.collaboratorsSchedule?.id === id) {
        next = {
          ...next,
          collaboratorsSchedule: {
            ...next.collaboratorsSchedule,
            ...(collaborators !== undefined ? { collaborators } : {}),
          },
        };
      }
      if (next.accessControlSchedule?.id === id) {
        next = {
          ...next,
          accessControlSchedule: {
            ...next.accessControlSchedule,
            ...(accessControl !== undefined ? { accessControl } : {}),
          },
        };
      }
      return next;
    },
  },
});

export default dvaModelExtend(_model, { namespace });
